import { memo, useState, useCallback, useEffect } from 'react';
import { Loader2, Sparkles, Trash2, Eye, EyeOff, FileText, Lock, ChevronRight, Check, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const BlogTab = memo(({ siteId, userPlan, onShowPricing, t, theme }) => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState('main'); // main | topics | generating | write
  const [topics, setTopics] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [generating, setGenerating] = useState({ active: false, done: 0, total: 0, current: '' });
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('warm and personal');
  const [writing, setWriting] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const isPro = userPlan && (userPlan.plan === 'pro' || userPlan.features?.blog_posts);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sites/${siteId}/blog`, { withCredentials: true });
      setPosts(res.data.posts || []);
    } catch { setPosts([]); }
    setLoaded(true);
  }, [siteId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const generateTopics = async () => {
    setLoadingTopics(true);
    try {
      // Try to get SEO profile first
      let profile = {};
      try {
        const p = await axios.get(`${API}/seo/profile/${siteId}`, { withCredentials: true });
        profile = p.data || {};
      } catch {}

      if (!profile.city) {
        // No SEO profile — generate generic wedding topics
        const res = await axios.post(`${API}/seo/blog-topics`, {
          site_id: siteId, studio_name: '', city: '', work_type: 'Wedding Photography',
        }, { withCredentials: true });
        setTopics(res.data.topics || []);
      } else {
        const res = await axios.post(`${API}/seo/blog-topics`, {
          site_id: siteId, ...profile,
        }, { withCredentials: true });
        setTopics(res.data.topics || []);
      }
      setSelected(new Set());
      setView('topics');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate topics');
    }
    setLoadingTopics(false);
  };

  const toggleTopic = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === topics.length) setSelected(new Set());
    else setSelected(new Set(topics.map((_, i) => i)));
  };

  const generateSelected = async () => {
    const toGenerate = [...selected].map(i => topics[i]).filter(Boolean);
    if (toGenerate.length === 0) return;
    setView('generating');
    setGenerating({ active: true, done: 0, total: toGenerate.length, current: '' });

    for (let i = 0; i < toGenerate.length; i++) {
      setGenerating(prev => ({ ...prev, done: i, current: toGenerate[i].title }));
      try {
        await axios.post(`${API}/seo/blog-generate`, {
          site_id: siteId, title: toGenerate[i].title, keyword: toGenerate[i].keyword || '',
        }, { withCredentials: true });
      } catch { /* continue */ }
    }
    setGenerating({ active: false, done: toGenerate.length, total: toGenerate.length, current: '' });
    await fetchPosts();
    setTimeout(() => setView('main'), 1500);
  };

  const handleAiWrite = async () => {
    if (!topic.trim() || writing) return;
    setWriting(true);
    try {
      const res = await axios.post(`${API}/sites/${siteId}/blog/ai-write`, { topic: topic.trim(), tone }, { withCredentials: true });
      setPosts(prev => [res.data.post, ...prev]);
      setTopic('');
      setView('main');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to write post');
    }
    setWriting(false);
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await axios.patch(`${API}/sites/${siteId}/blog/${post.id}`, { status: newStatus }, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: newStatus } : p));
    } catch {}
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/sites/${siteId}/blog/${postId}`, { withCredentials: true });
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch {}
  };

  const handleSaveEdit = async () => {
    if (!editingPost || saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/sites/${siteId}/blog/${editingPost}`, { title: editTitle, content: editContent }, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === editingPost ? { ...p, title: editTitle, content: editContent } : p));
      setEditingPost(null);
    } catch {}
    setSaving(false);
  };

  if (!isPro) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 12px' }}>
        <Lock size={24} style={{ color: t.muted, margin: '0 auto 12px', opacity: 0.4 }} />
        <p style={{ fontFamily: 'var(--serif)', fontSize: '16px', color: t.fg, marginBottom: '6px' }}>Blog is a Pro feature</p>
        <p style={{ fontSize: '12px', color: t.muted, marginBottom: '16px', lineHeight: 1.6 }}>
          Upgrade to Pro to write blog posts with Iris and boost your SEO.
        </p>
        <button onClick={onShowPricing} className="btn-primary" style={{ fontSize: '11px', padding: '10px 24px' }} data-testid="blog-upgrade-btn">
          Upgrade to Pro
        </button>
      </div>
    );
  }

  // TOPIC GENERATION VIEW
  if (view === 'topics' && topics.length > 0) {
    return (
      <div style={{ padding: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <button onClick={() => setView('main')} style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: t.muted, background: 'none', border: 'none', cursor: 'pointer' }}>&larr; Back</button>
          <button onClick={selectAll} style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: t.gold, background: 'none', border: 'none', cursor: 'pointer' }}>
            {selected.size === topics.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <p style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: t.gold, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Pick your topics</p>
        <p style={{ fontSize: '11px', color: t.muted, marginBottom: '14px', lineHeight: 1.5 }}>Select the posts you want Iris to write. Each targets a keyword couples search for.</p>
        {topics.map((tp, i) => (
          <div key={i} onClick={() => toggleTopic(i)} data-testid={`topic-${i}`} style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 8px', cursor: 'pointer',
            borderBottom: `1px solid ${t.border}`, background: selected.has(i) ? 'rgba(201,168,76,0.04)' : 'transparent', transition: 'background .15s',
          }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '2px', border: `1.5px solid ${selected.has(i) ? t.gold : t.border}`, background: selected.has(i) ? t.gold : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {selected.has(i) && <Check size={11} color={theme === 'light' ? '#fff' : '#000'} />}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '12px', color: t.fg, marginBottom: '2px' }}>{tp.title}</p>
              {tp.keyword && <p style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: t.muted }}>{tp.keyword}</p>}
            </div>
          </div>
        ))}
        <button onClick={generateSelected} disabled={selected.size === 0} data-testid="generate-selected-btn" style={{
          width: '100%', marginTop: '14px', padding: '12px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase',
          background: selected.size > 0 ? t.gold : t.faint, color: selected.size > 0 ? (theme === 'light' ? '#fff' : '#000') : t.muted,
          border: 'none', cursor: selected.size > 0 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <Sparkles size={12} /> Generate {selected.size} Post{selected.size !== 1 ? 's' : ''}
        </button>
      </div>
    );
  }

  // GENERATING VIEW
  if (view === 'generating') {
    return (
      <div style={{ padding: '20px 4px', textAlign: 'center' }}>
        <Loader2 size={24} className="animate-spin" style={{ color: t.gold, margin: '0 auto 16px' }} />
        <p style={{ fontFamily: 'var(--serif)', fontSize: '16px', color: t.fg, marginBottom: '6px' }}>
          {generating.done < generating.total ? 'Iris is writing...' : 'All posts created!'}
        </p>
        <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: t.gold, marginBottom: '16px' }}>{generating.done}/{generating.total}</p>
        <div style={{ height: '4px', background: t.faint, borderRadius: '2px', overflow: 'hidden', marginBottom: '12px' }}>
          <div style={{ height: '100%', width: `${(generating.done / Math.max(generating.total, 1)) * 100}%`, background: t.gold, transition: 'width .5s' }} />
        </div>
        {generating.current && <p style={{ fontSize: '11px', color: t.muted, fontStyle: 'italic' }}>{generating.current}</p>}
        {generating.done >= generating.total && <p style={{ fontSize: '12px', color: '#5ba89d', marginTop: '12px' }}><Check size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> Done! Returning to your posts...</p>}
      </div>
    );
  }

  // CUSTOM WRITE VIEW
  if (view === 'write') {
    return (
      <div style={{ padding: '4px' }}>
        <button onClick={() => setView('main')} style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: t.muted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px' }}>&larr; Back</button>
        <p style={{ fontFamily: 'var(--serif)', fontSize: '14px', color: t.fg, marginBottom: '10px' }}>What should Iris write about?</p>
        <input type="text" value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Best wedding venues in Austin" data-testid="blog-topic-input"
          style={{ width: '100%', padding: '8px 12px', fontSize: '12px', fontFamily: 'var(--sans)', background: theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)', border: `1px solid ${t.border}`, color: t.fg, outline: 'none', marginBottom: '8px' }}
          onKeyDown={e => e.key === 'Enter' && handleAiWrite()} />
        <select value={tone} onChange={e => setTone(e.target.value)} style={{ width: '100%', padding: '6px 10px', fontSize: '11px', fontFamily: 'var(--sans)', background: theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)', border: `1px solid ${t.border}`, color: t.fg, cursor: 'pointer', marginBottom: '12px' }}>
          <option value="warm and personal">Warm & Personal</option>
          <option value="professional and informative">Professional & Informative</option>
          <option value="fun and casual">Fun & Casual</option>
          <option value="editorial and authoritative">Editorial & Authoritative</option>
        </select>
        <button onClick={handleAiWrite} disabled={!topic.trim() || writing} data-testid="blog-generate-btn" style={{
          width: '100%', padding: '10px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase',
          background: (!topic.trim() || writing) ? t.faint : t.gold, color: (!topic.trim() || writing) ? t.muted : (theme === 'light' ? '#fff' : '#000'),
          border: 'none', cursor: (!topic.trim() || writing) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          {writing ? <><Loader2 size={12} className="animate-spin" /> Writing...</> : <><Sparkles size={12} /> Write Post</>}
        </button>
      </div>
    );
  }

  // MAIN VIEW
  return (
    <div>
      {/* Actions */}
      {posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 12px' }}>
          <Search size={24} style={{ color: t.gold, margin: '0 auto 12px', opacity: 0.6 }} />
          <p style={{ fontFamily: 'var(--serif)', fontSize: '16px', color: t.fg, marginBottom: '4px' }}>Blogs boost your Google ranking</p>
          <p style={{ fontSize: '11px', color: t.muted, marginBottom: '20px', lineHeight: 1.6 }}>Let Iris research what couples in your area are searching for and write posts that bring them to your site.</p>
          <button onClick={generateTopics} disabled={loadingTopics} data-testid="blog-gen-topics-btn" style={{
            width: '100%', padding: '12px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase',
            background: t.gold, color: theme === 'light' ? '#fff' : '#000', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '10px',
          }}>
            {loadingTopics ? <><Loader2 size={12} className="animate-spin" /> Finding topics...</> : <><Sparkles size={12} /> Generate 10 Topic Ideas</>}
          </button>
          <button onClick={() => setView('write')} style={{ width: '100%', padding: '10px', fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.06em', background: 'none', border: `1px solid ${t.border}`, color: t.muted, cursor: 'pointer' }}>
            or write about something specific
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <p style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: t.muted, letterSpacing: '.08em' }}>
              {posts.length} post{posts.length !== 1 ? 's' : ''} · {posts.filter(p => p.status === 'published').length} published
            </p>
          </div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
            <button onClick={generateTopics} disabled={loadingTopics} data-testid="blog-more-topics-btn" style={{
              flex: 1, padding: '8px', fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.06em', textTransform: 'uppercase',
              background: 'rgba(201,168,76,0.06)', border: `1px solid rgba(201,168,76,0.15)`, color: t.gold, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}>
              {loadingTopics ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} More Topics
            </button>
            <button onClick={() => setView('write')} style={{
              flex: 1, padding: '8px', fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.06em', textTransform: 'uppercase',
              background: 'none', border: `1px solid ${t.border}`, color: t.muted, cursor: 'pointer',
            }}>
              Custom Post
            </button>
          </div>
        </>
      )}

      {/* Edit Panel */}
      {editingPost && (
        <div data-testid="blog-edit-panel" style={{ padding: '12px', border: `1px solid ${t.border}`, marginBottom: '12px' }}>
          <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '13px', fontFamily: 'var(--serif)', fontWeight: 600, background: theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)', border: `1px solid ${t.border}`, color: t.fg, outline: 'none', marginBottom: '6px' }} />
          <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={6} style={{ width: '100%', padding: '8px', fontSize: '11px', fontFamily: 'var(--sans)', background: theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)', border: `1px solid ${t.border}`, color: t.fg, outline: 'none', resize: 'vertical', lineHeight: 1.6, marginBottom: '6px' }} />
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={() => setEditingPost(null)} style={{ flex: 1, padding: '6px', fontFamily: 'var(--mono)', fontSize: '8px', background: 'transparent', border: `1px solid ${t.border}`, color: t.fg, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSaveEdit} disabled={saving} style={{ flex: 1, padding: '6px', fontFamily: 'var(--mono)', fontSize: '8px', background: t.gold, border: 'none', color: theme === 'light' ? '#fff' : '#000', cursor: 'pointer' }}>{saving ? '...' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* Posts List */}
      {posts.map(post => (
        <div key={post.id} data-testid={`blog-post-${post.id}`} style={{ padding: '10px', marginBottom: '4px', border: `1px solid ${t.border}`, transition: 'all .2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 600, color: t.fg, flex: 1 }}>{post.title}</p>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '7px', letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 5px', flexShrink: 0, marginLeft: '6px', background: post.status === 'published' ? 'rgba(91,168,157,0.1)' : 'rgba(242,239,233,0.06)', color: post.status === 'published' ? '#5ba89d' : t.muted, border: `1px solid ${post.status === 'published' ? 'rgba(91,168,157,0.2)' : t.border}` }}>
              {post.status}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
            <button onClick={() => { setEditingPost(post.id); setEditTitle(post.title); setEditContent(post.content || ''); }} style={{ padding: '2px 6px', fontFamily: 'var(--mono)', fontSize: '7px', background: 'transparent', border: `1px solid ${t.border}`, color: t.muted, cursor: 'pointer' }}>Edit</button>
            <button onClick={() => handleToggleStatus(post)} style={{ padding: '2px 6px', fontFamily: 'var(--mono)', fontSize: '7px', background: 'transparent', border: `1px solid ${t.border}`, color: post.status === 'published' ? '#5ba89d' : t.gold, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px' }}>
              {post.status === 'published' ? <><EyeOff size={7} /> Hide</> : <><Eye size={7} /> Publish</>}
            </button>
            <button onClick={() => handleDelete(post.id)} style={{ padding: '2px 6px', fontFamily: 'var(--mono)', fontSize: '7px', background: 'transparent', border: '1px solid rgba(196,71,114,0.2)', color: '#c47272', cursor: 'pointer' }}><Trash2 size={7} /></button>
          </div>
        </div>
      ))}
    </div>
  );
});

BlogTab.displayName = 'BlogTab';
export default BlogTab;
